import * as React from "react";
import { connect } from "react-redux";
import { IAppState } from "../models/IAppState"
import ITreatmentPlanTemplateResponseObj from "../models/ITreatmentPlanTemplateResponse";
import TreatmentPlanCard from "../components/TreatmentPlanCard";
import { TreatmentPlanActions } from "../actions/TreatmentPlanActions";
import { RouteUtils, TREATMENT_PLANS_ADD } from "../routes";
import { toastNotificationAppearance, FeatureConfigValues, AppConstants } from "../utils/AppConstants";
import AppUtils from "../utils/AppUtils";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { EventNames, SectionContext } from "../utils/EventTrackerConstants";
import { Tracker } from "../utils/Tracker";
import { IPdfTemplates, IConstituentTemplate } from "../models/PdfTemplatesModels";
import { NewFeatureBannerCta, NewFeatureBanner } from "../components/NewFeatureBanner";
import { IRecomTemplate, IRecomTemplateWithPrescriptionData } from "../models/IRecoTemplates";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";

export interface PatientsSearchPageProps {
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & PatientsSearchPageProps

const TreatmentPlansPage: React.FC<Props> = (props) => {

    const [searchText, setSearchText] = React.useState("")
    const [templateList, setTemplateList] = React.useState<Array<ITreatmentPlanTemplateResponseObj>>([])
    const [noOfStroke, setNoOfStroke] = React.useState(0);
    const [debouncedValue, setDebouncedValue] = React.useState(searchText);
    const [pdfTemplates, setPdfTemplates] = React.useState<IPdfTemplates>(null);
    const [recoTemplates, setRecoTemplates] = React.useState<IRecomTemplateWithPrescriptionData>(null);


    const newFeatureStepsList: string[] = ["Save prescriptions for your commonly used diagnosis as Treatment Plans",
        "In one click create prescription by adding details from Treatment Plan",
        "More Treatment Plans means lesser time spent on creating prescriptions"];
    const newFeaatureBannerCta: NewFeatureBannerCta[] = [
        {
            text: "How to create",
            onClick: () => {
                Tracker.track(EventNames.Treatment_Plan_How_To_Create);
                window.open("https://youtu.be/ksgk6ASwrxY");
            }
        },
        {
            text: "How to Use",
            onClick: () => {
                Tracker.track(EventNames.Treatment_Plan_How_To_Use);
                window.open("https://youtu.be/x08MuVa0vbI");
            }
        }
    ]

    React.useEffect(() => {
        fetchTemplatesByName();
    }, [debouncedValue]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(searchText);
        }, 300);
        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    React.useEffect(() => {
        // On Mount
        if (!props.isTreatmentPlanEnabled) {
            RouteUtils.navigateToUrl("/")
        }
        else {
            getConsultationPdfTemplates();
            fetchRecomTemplates();
        }
    }, []);

    const getConsultationPdfTemplates = () => {
        props.fetchConsultationPdfTemplates(props.doctorProfile.id, props.doctorProfile.agentCenterMapping[0].centerId, props.doctorProfile.primarySubServiceType.code)
            .then((response: IConstituentTemplate) => {
                const templateIdToNameMap = AppUtils.setPdfTemplatesIdToNameMap(response.constituentTemplates || []);
                const templateNameToDataMap = AppUtils.setPdfTemplateNameToDataMap(response.constituentTemplates || []);
                const responseToSave = { ...response, templateIdToNameMap, templateNameToDataMap };
                setPdfTemplates(responseToSave)
            })
            .catch(() => {
                setPdfTemplates(null)
            });
    }
    const fetchRecomTemplates = () => {
        props.fetchRecomTemplates(props.doctorProfile.id, AppConstants.NUMBER_OF_RECOM_TEMPLATES_TO_FETCH, props.doctorProfile.primarySubServiceType.code)
            .then((recoTemplates: IRecomTemplateWithPrescriptionData) => {
                setRecoTemplates(recoTemplates);
            })
            .catch(err => {
                console.log(err);
            });
    }

    const fetchTemplatesByName = () => {
        props.fetchTreatmentPlans(props.doctorProfile.id, debouncedValue)
            .then((response: Array<ITreatmentPlanTemplateResponseObj>) => {
                setTemplateList(response)
            })
            .catch(() => {
                setTemplateList([])
                console.error("Error occoured fetching treatment plans")
            })
    }

    const handleSearch = (event: any) => {
        setNoOfStroke(noOfStroke + 1)
        setSearchText(event.target.value)
    }

    const onBlur = (event: any) => {
        Tracker.track(EventNames.KeyStroke, { value: event.target.value, field: EventNames.Search, noOfKeystrokes: event.target.value?.length, section: EventNames.All_Treatment, searchString: event.target.value });
        LocalStorageUtils.setClickEvents({ field: EventNames.Search, flow: EventNames.Treatment_Plan, section: EventNames.All_Treatment, value: event.target.value })
    }

    const onDelete = (listItem: ITreatmentPlanTemplateResponseObj) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Delete, flow: EventNames.Treatment_Plan, section: EventNames.All_Treatment, value: listItem.id })
        props.deleteTreatmentPlans(listItem.id)
            .then((response: any) => {
                console.log("Plan deleted sucessfulley")
                fetchTemplatesByName()
                AppUtils.triggerToastNotification(`Successfully deleted the Treatment Plan: ${listItem.templateName}`, toastNotificationAppearance.success)
            })
            .catch(() => {
                console.error("Error occoured when deleting treatment plan")
            })
    }

    const onEdit = (listItem: ITreatmentPlanTemplateResponseObj) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Edit, flow: EventNames.Treatment_Plan, section: EventNames.All_Treatment, value: listItem.id })
        RouteUtils.navigateToHrefUrl(null, RouteUtils.getEditTreatmentPlanUrl(listItem.id))
    }

    const onRecoSelect = (listItem: ITreatmentPlanTemplateResponseObj, referenceId: number, patientId: number) => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Edit, flow: EventNames.Treatment_Plan, section: EventNames.All_Treatment, value: listItem.id })
        RouteUtils.navigateToHrefUrl(null, RouteUtils.getRecoTreatmentPlanUrl(referenceId.toString(), patientId.toString()));
    }

    const onAddNewClick = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Add_New, flow: EventNames.Treatment_Plan, section: EventNames.All_Treatment })
        RouteUtils.navigateToHrefUrl(null, TREATMENT_PLANS_ADD)
    }
    const isMobile: boolean = AppUtils.getIsMobile();
    return (
        <SectionContext.Provider value={EventNames.All_Treatment}>
            <div className="treatment-plans-page">
                <div className="col-md-10">
                    {/* <img src="/images/treatment-plan-2x.png" className="treatment-plan-banner" /> */}
                    {!isMobile &&
                        <NewFeatureBanner stepsList={newFeatureStepsList} featureName="Treatment Plans" ctaList={newFeaatureBannerCta} />
                    }
                    <div className="header">
                        <div className="left-header col-xs-12 col-md-4">
                            <div className="main-header">
                                {"Treatment Plans"}
                            </div>
                            <div className="sub-header">
                                {"Edit or create new prescription templates for use during consultations"}
                            </div>
                        </div>
                        <div className="right-header col-xs-12 col-md-5">
                            <div className="search-bar col-xs-9 bordered">
                                <img src="/images/search.svg" height={16} width={16} />
                                <input type="text" className="search-text" value={searchText} onChange={handleSearch} onBlur={onBlur} placeholder={""} />
                            </div>
                            {/* TODO: Add after filters */}
                            {/* <div className="filter-options col-md-2 bordered">
                            <img src="/images/icons/down-arrow.svg" height={16} width={16} />
                            <span className="vertical-divider"></span>
                            <img src="/images/icons/down-arrow.svg" height={16} width={16} />
                        </div> */}
                        </div>
                    </div>
                    <div className="body">
                        <div className="recommended-template-container">
                            <div>
                                <div className="title">
                                    <span className="recommendation-heading">Frequent Diagnosis</span>
                                </div>
                                <div>
                                    We suggest creating a template for these to save time or using a recommended template from below.
                                </div>
                                {recoTemplates && recoTemplates.prescriptions && recoTemplates.prescriptions.length &&
                                    <div className="recommendation-cards-container">
                                        {recoTemplates.prescriptions.map((template: ITreatmentPlanTemplateResponseObj, index: number) => {
                                            const templateName: string = recoTemplates.diagnoses[index] && recoTemplates.diagnoses[index].diseaseName;
                                            const noOfTimesRepeated: number = recoTemplates && recoTemplates.scores[index];
                                            const referenceId: number = recoTemplates && recoTemplates.appointment[index] && recoTemplates.appointment[index].referenceId;
                                            const patientId: number = recoTemplates && recoTemplates.appointment[index] && recoTemplates.appointment[index].patientId;

                                            return (
                                                <div key={template.id} className="treatment-card">
                                                    <TreatmentPlanCard
                                                        listItem={{ ...template, templateName }}
                                                        pdfTemplates={pdfTemplates}
                                                        editMode={false}
                                                        onListItemClick={(item: ITreatmentPlanTemplateResponseObj) => onRecoSelect(item, referenceId, patientId)}
                                                        noOfTimesRepeated={noOfTimesRepeated}
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>
                                }
                            </div>
                        </div>
                        <div key="treatmentPlan0" className="treatment-card">
                            <div onClick={onAddNewClick} className="add-new-template">
                                <img src="/images/plus.svg" height={60} width={60} />
                                <span className={"label"}>{"Create New Template"}</span>
                            </div>
                        </div>
                        {templateList.map((template: ITreatmentPlanTemplateResponseObj) => {
                            return (
                                <div key={template.id} className="treatment-card">
                                    <TreatmentPlanCard
                                        listItem={template}
                                        pdfTemplates={pdfTemplates}
                                        editMode={true}
                                        onDeleteClick={onDelete}
                                        onEditClick={onEdit} />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </SectionContext.Provider>
    )
}

function mapStateToProps(state: IAppState) {
    return {
        doctorProfile: state.doctorProfile,
        isTreatmentPlanEnabled: state.featureConfigInfo["OrionEnablePrescriptionTemplate"]
            ? state.featureConfigInfo["OrionEnablePrescriptionTemplate"] === FeatureConfigValues.TRUE
            : true
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        deleteTreatmentPlans: (templateId: string): Promise<any> => {
            return TreatmentPlanActions.deleteTreatmentPlan(templateId)
        },
        fetchTreatmentPlans: (doctorId: number, query: string): Promise<Array<ITreatmentPlanTemplateResponseObj>> => {
            if (query) {
                return TreatmentPlanActions.fetchTreatmentPlansByName(doctorId, query)
            }
            else {
                return TreatmentPlanActions.fetchAllTreatmentPlanTemplates(doctorId, dispatch)
            }
        },
        fetchConsultationPdfTemplates: (doctorId: number, centerId: number, doctorType: string): Promise<IConstituentTemplate> => {
            return TreatmentPlanActions.fetchConsultationPdfTemplates(doctorId, centerId, doctorType)
        },
        fetchRecomTemplates: (doctorId: number, count: number, doctorType: string): Promise<IRecomTemplateWithPrescriptionData> => {
            return TreatmentPlanActions.fetchRecomTemplates(doctorId, count, doctorType);
        }
    }
};

export default connect<ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>, PatientsSearchPageProps>(
    mapStateToProps,
    mapDispatchToProps
)(TreatmentPlansPage);
